<template>
  <teleport to="body">
    <transition name="icm">
      <div v-if="visible" class="icm-overlay" role="dialog" aria-modal="true">
        <div class="icm-panel">

          <!-- Header -->
          <div class="icm-header">
            <h5 class="icm-title">{{ title }}</h5>
            <p class="icm-subtitle">{{ subtitle }}</p>
          </div>

          <!-- Crop area — fills all space between header and footer -->
          <div class="icm-crop-area">
            <Cropper
              ref="cropperRef"
              class="icm-cropper"
              :src="src"
              :stencil-props="{ aspectRatio }"
            />
          </div>

          <!-- Footer — always pinned at bottom -->
          <div class="icm-footer">
            <button
              type="button"
              class="icm-btn icm-btn--cancel"
              @click="$emit('cancel')"
              :disabled="confirming"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="icm-btn icm-btn--confirm"
              @click="handleConfirm"
              :disabled="confirming"
            >
              <span
                v-if="confirming"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              <i v-else class="fas fa-crop-alt me-2"></i>
              Confirmar
            </button>
          </div>

        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { ref } from 'vue';
import { Cropper } from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';
import { toast } from 'vue3-toastify';

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  src: {
    type: String,
    default: '',
  },
  aspectRatio: {
    type: Number,
    default: 1,
  },
  title: {
    type: String,
    default: 'Ajuste a foto',
  },
  subtitle: {
    type: String,
    default: 'Posicione e enquadre como preferir.',
  },
});

const emit = defineEmits(['confirm', 'cancel']);

const cropperRef = ref(null);
const confirming = ref(false);

function handleConfirm() {
  if (!cropperRef.value || confirming.value) return;

  confirming.value = true;

  const result = cropperRef.value.getResult();
  const canvas = result?.canvas;

  if (!canvas) {
    toast.error('Não foi possível recortar a imagem.');
    confirming.value = false;
    return;
  }

  canvas.toBlob(
    (blob) => {
      confirming.value = false;
      if (!blob) {
        toast.error('Não foi possível processar a imagem.');
        return;
      }
      emit('confirm', {
        blob,
        dataUrl: canvas.toDataURL('image/jpeg', 0.92),
      });
    },
    'image/jpeg',
    0.92,
  );
}
</script>

<style scoped>
/* ── Overlay ─────────────────────────────────────────────── */
.icm-overlay {
  position: fixed;
  inset: 0;
  z-index: 10300;
  background: #111;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* ── Panel (max-width container) ─────────────────────────── */
.icm-panel {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 560px;
  height: 100%;
  height: 100dvh;
}

/* ── Header ──────────────────────────────────────────────── */
.icm-header {
  flex-shrink: 0;
  background: #fff;
  padding: 1.1rem 1.5rem 0.9rem;
  text-align: center;
  border-bottom: 1px solid #f0f0f0;
}

.icm-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 0.2rem;
}

.icm-subtitle {
  font-size: 0.8rem;
  color: #999;
  margin: 0;
}

/* ── Crop area ───────────────────────────────────────────── */
.icm-crop-area {
  flex: 1;
  min-height: 0; /* critical: allows flex child to shrink below content size */
  overflow: hidden;
  background: #111;
}

.icm-cropper {
  width: 100%;
  height: 100%;
}

/* ── Footer ──────────────────────────────────────────────── */
.icm-footer {
  flex-shrink: 0;
  background: #fff;
  display: flex;
  gap: 0.75rem;
  padding: 0.875rem 1.25rem;
  padding-bottom: max(0.875rem, env(safe-area-inset-bottom));
  border-top: 1px solid #f0f0f0;
}

/* ── Buttons ─────────────────────────────────────────────── */
.icm-btn {
  flex: 1;
  padding: 0.75rem 1rem;
  border-radius: 0.625rem;
  font-size: 0.9rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: opacity 0.15s, background-color 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icm-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.icm-btn--cancel {
  background: #f0f0f4;
  color: #555;
  border: 1px solid #e0e0e8;
}

.icm-btn--cancel:hover:not(:disabled) {
  background: #e4e4ea;
}

.icm-btn--confirm {
  background: #ff50a3;
  color: #fff;
  border: 1px solid #ff50a3;
}

.icm-btn--confirm:hover:not(:disabled) {
  background: #e6408f;
  border-color: #e6408f;
}

/* ── Transition ──────────────────────────────────────────── */
.icm-enter-active,
.icm-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.icm-enter-from,
.icm-leave-to {
  opacity: 0;
  transform: translateY(100%);
}
</style>
